import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store/store';
import { COURSE_SESSION_PATH } from '../routes/courseSessionPath';
import {
  isPlainOraViewOn,
  setPlainOraView,
  useIsMcGrawHillMember,
} from './plainOraView';

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT' ||
    target.isContentEditable ||
    target.closest('[contenteditable]:not([contenteditable="false"])') !== null
  );
};

/**
 * Game rounds, study / exam runners and a course session: flipping the whole
 * app there would pull the member out of a session (the plain view also
 * changes `homeVariant`, so whether they are a course student).
 */
const isInSessionPath = (pathname: string): boolean =>
  pathname.startsWith('/games/') ||
  pathname.startsWith('/study/') ||
  pathname.startsWith(COURSE_SESSION_PATH) ||
  /^\/self-assessments\/[^/]+\/blocks\//.test(pathname);

/**
 * mcgraw-hill (ORA-1268): Shift+D toggles the plain Ora view for a McGraw
 * Hill member (read from the real instance, so it also turns the view back
 * off), outside text fields and game rounds (the `T` hotkey's guards). Plain
 * `d` is taken: the study panel's Document tab, the Dashboard's admin demo
 * mode. McGraw Hill's own pages send the plain view home through their
 * gates. A profile from another instance clears the flag.
 */
export const usePlainOraViewHotkey = (): void => {
  const profile = useSelector((state: RootState) => state.user.profile);
  const isMember = useIsMcGrawHillMember();

  useEffect(() => {
    if (profile && !isMember) setPlainOraView(false);
  }, [profile, isMember]);

  useEffect(() => {
    if (!isMember) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingTarget(event.target)) return;
      const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
      if (key !== 'd' || !event.shiftKey) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Held down, the key repeats: one press, one toggle.
      if (event.repeat) return;
      if (isInSessionPath(window.location.pathname)) return;

      setPlainOraView(!isPlainOraViewOn());
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMember]);
};
